// Cold-start reveal for the top pill. During `body.app-loading` the pill is a
// compact logo + "MagViz" badge (CSS clips the rest off the right via
// `max-width: var(--topbar-collapsed)` + overflow:hidden). When bootstrap drops
// `app-loading`, this unfurls the pill horizontally by animating `max-width` to
// the measured content width, then releases it so the pill stays content-sized
// and responsive (fit folds, dataset relabels) afterwards.

const RELEASE_FALLBACK_MS = 700; // safety net if transitionend never fires

export function installTopbarReveal(): () => void {
    const pill = document.getElementById('topbar-menu');
    if (!pill) return () => { /* no pill */ };

    let teardown: (() => void) | null = null;

    const expand = (): void => {
        const collapsed =
            getComputedStyle(pill).getPropertyValue('--topbar-collapsed').trim() || '140px';
        // Pin the collapsed width inline first. This runs in the MutationObserver
        // microtask — before paint — so removing `app-loading` (which drops the
        // CSS collapse rule) can't flash the pill to full width before we measure.
        pill.style.overflow = 'hidden';
        pill.style.maxWidth = collapsed;
        void pill.offsetWidth; // commit the pinned width
        const target = pill.scrollWidth; // full inline content (ignores the clip)
        pill.style.maxWidth = `${target}px`; // CSS max-width transition animates the unfurl

        const release = (): void => {
            pill.style.maxWidth = ''; // content-sized + responsive from here on
            pill.style.overflow = ''; // restore so the actions popup isn't clipped
            teardown?.();
            teardown = null;
        };
        const onEnd = (e: TransitionEvent): void => {
            if (e.propertyName === 'max-width') release();
        };
        pill.addEventListener('transitionend', onEnd);
        const fallback = window.setTimeout(release, RELEASE_FALLBACK_MS);
        teardown = () => {
            pill.removeEventListener('transitionend', onEnd);
            clearTimeout(fallback);
        };
    };

    let mo: MutationObserver | null = null;
    if (document.body.classList.contains('app-loading')) {
        mo = new MutationObserver(() => {
            if (document.body.classList.contains('app-loading')) return;
            mo?.disconnect();
            mo = null;
            expand();
        });
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    } else {
        // Already past loading (hot reload / fast boot) — unfurl next frame.
        requestAnimationFrame(expand);
    }

    return () => {
        mo?.disconnect();
        teardown?.();
    };
}
